import { EmailVerificationToken } from '@/core/models/email-verification-token.model';
import { AfterViewInit, Component, Inject, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TextBoxComponent } from '@progress/kendo-angular-inputs';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { AuthenticationConfig } from '../../authentication.module';
import { PasswordValidator } from '../../validators/password.validator';
import { PasswordMatchValidator } from '../../validators/passwordmatch.validator';
import { ResetPasswordCommand } from '@/api/models/ace/xFound/Portal/Core/CAQ/Authentication/Commands/reset-password-command';
import { UserCredentialAuthenticationRequest } from '@/api/models/ace/xFound/Portal/Core/CAQ/Authentication/Requests/user-credential-authentication-request';
import { TokenExpiredNotification } from '@/core/utils/notifications/authentication/token-expired.notification';
import { ExtendedAuthenticationService } from '../../services/extended-authentication.service';
import { IdToken } from '@/core/models/id-token.model';
import { AccessToken } from '@/core/models/access-token.model';
import { LoadingAnimationService } from '@/core/services/loading-animation.service';
import { AUTHENTICATION_CONFIG } from '../../injection-tokens';
import { I18nService } from '@/core/services/i18nService';
import { emailValidator } from '@/core/validators/customform.validators';
import { toggleTextboxPasswordVisibility } from '@/core/utils/toggle-textbox-password-visibility';
import { eyeIcon } from '@progress/kendo-svg-icons';
import { PortalkonfigurationDisplayModel } from '@/api/model-index';
import { deserializeHttpError } from '@/core/utils/deserialize-http-error';
import { HttpErrorResponse } from '@angular/common/http';
import { UserFriendlyException } from '@/core/utils/notifications/user-friendly-exception.notification';
import { NotificationService } from '@progress/kendo-angular-notification';

@Component({
    selector: 'app-reset-password',
    templateUrl: './reset-password.component.html',
    styleUrls: ['./reset-password.component.scss'],
    standalone: false
})
export class ResetPasswordComponent implements OnInit, AfterViewInit {

  error: string;
  resetPasswordForm: UntypedFormGroup;
  redirect : string;
  resetSubscription : Subscription;
  resetPasswordCommand = new ResetPasswordCommand();
  public enableCaptcha: boolean = false;

  @ViewChild("password") public passwordTextBox: TextBoxComponent;
  @ViewChild("passwordW") public passwordWiederholenTextBox: TextBoxComponent;

  protected portalkonfiguration: PortalkonfigurationDisplayModel;

  public get passwortFormControl(): AbstractControl {
    return this.resetPasswordForm.controls["passwort"];
  }

  public get passwortWiederholenFormControl(): AbstractControl {
    return this.resetPasswordForm.controls["passwortWiederholen"];
  }

  public get passwortErrorMessages(): string[] {
    if(!this.passwortFormControl.dirty)
      return [];
    let validationErrors: ValidationErrors | null = this.passwortFormControl.errors;

    if(!validationErrors)
      return [];

    return Object.values(validationErrors).filter(val => typeof val === 'string') as string[];
  }

  public get passwortWiderholenErrorMessages(): string[] {
    if(!this.passwortWiederholenFormControl.dirty)
      return [];
    let validationErrors: ValidationErrors | null = this.passwortWiederholenFormControl.errors;

    if(!validationErrors)
      return [];

    return this.toPasswortWiderholenErrorMessage(validationErrors);
  }

  public get passwortValidated() : boolean{
    return this.resetPasswordForm.controls.passwort.valid || this.resetPasswordForm.controls.passwort.untouched;
  }

  public get passwortWiederholenValidated() : boolean{
    return this.resetPasswordForm.controls.passwortWiederholen.valid || this.resetPasswordForm.controls.passwortWiederholen.untouched;
  }

  public get passwortError() : ValidationErrors | null{
    return this.resetPasswordForm.controls.passwort.errors
  }

  constructor(protected router: Router,
    protected route: ActivatedRoute,
    protected formBuilder: UntypedFormBuilder,
    protected authenticationService: ExtendedAuthenticationService,
    protected loadingAnimationService: LoadingAnimationService,
    @Inject(AUTHENTICATION_CONFIG) protected authenticationConfig: AuthenticationConfig,
    protected languageservice : I18nService, protected notificationService: NotificationService,) {
    this.portalkonfiguration = this.route.snapshot.parent?.data.portalkonfiguration;
    this.createForm();
  }
  
  ngAfterViewInit(): void {
    this.passwordTextBox.input.nativeElement.type = "password";
    this.passwordWiederholenTextBox.input.nativeElement.type = "password";
  }

  public icons = { eye: eyeIcon };

  toggleVisibility(box : TextBoxComponent){
    toggleTextboxPasswordVisibility(box)
  }

  public get isLoading() : boolean{
    return this.resetSubscription && !this.resetSubscription.closed;
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe((params) => {
      try{
        const emailVerificationToken = new EmailVerificationToken(params.token);
        this.resetPasswordCommand.verificationToken = emailVerificationToken.toJwtToken();
        
        if(emailVerificationToken.isExpired)
          throw new TokenExpiredNotification("token expired");

        this.resetPasswordForm.controls.email.setValue(emailVerificationToken.email);
      }
      catch(error){

        if(error instanceof TokenExpiredNotification)
          throw error;
        else if(error instanceof Error)
          throw new Error("invalid token");

        this.router.navigateByUrl("/authentication/mailverification");
      }
    });
  }

  reset(){
    this.resetPasswordCommand.password = this.resetPasswordForm.get("passwort")?.value;
    this.resetPasswordCommand.captchaChallengeResponse = this.resetPasswordForm.controls.captchaChallengeResponse.value;
    this.resetSubscription = this.authenticationService.apiAuthenticationResetPasswordPatch(this.resetPasswordCommand.AsRequestBody())
    .pipe(finalize(() => {
      this.resetPasswordForm.markAsPristine();
    }))
    .subscribe(() => {
      if (this.resetPasswordCommand.verificationToken) {
        let loginCommand = new UserCredentialAuthenticationRequest();
        loginCommand.username = new EmailVerificationToken(btoa(this.resetPasswordCommand.verificationToken)).email;
        loginCommand.password = this.resetPasswordCommand.password;
        loginCommand.scope = this.authenticationConfig.scope;
        this.resetSubscription = this.authenticationService.login(loginCommand).subscribe(idToken => {
          if (idToken)
            this.authenticationService.saveIdToken(new IdToken(idToken));
          let promise = this.router.navigate(['/'], {replaceUrl: true});
          this.loadingAnimationService.showSpinnerUsingPromise(promise);
        });
      }

      this.loadingAnimationService.showSpinnerUsingSubscription(this.resetSubscription);
    });

    this.loadingAnimationService.showSpinnerUsingSubscription(this.resetSubscription);
  }

  protected toPasswortWiderholenErrorMessage(validationErrors: ValidationErrors) : string[] {
    let errorMessages: string[] = [];

    for(let key in validationErrors){
      switch(key){
        case "required":
          errorMessages.push(this.languageservice.instant('register.password_required'));
          break;
        case "passwordMatch":
          errorMessages.push(this.languageservice.instant('register.password_equalrequired'));
          break;
        default:
          errorMessages.push(this.languageservice.instant('register.invalid'));
      }
    }

    return errorMessages;
  }

  protected createForm() {
    this.resetPasswordForm = this.formBuilder.group({
      email: ['', [Validators.required, emailValidator]],
      passwort: ['', [Validators.required, new PasswordValidator(this.languageservice, this.portalkonfiguration.strongPasswordPolicy, ["email"])]],
      passwortWiederholen: ['', [Validators.required]],
      captchaChallengeResponse: ['']
    },{validators: PasswordMatchValidator("passwort","passwortWiederholen")});
  }
}
